import React from 'react'
import {
	Box, Card, CardHeader, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	ToggleButton, ToggleButtonGroup, Switch, IconButton, Grid, TextField, Autocomplete
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DEMO = [
	{ name: 'alphacars', flag: '🇬🇧', ratings: [4.2, 4.5, 3.9] },
	{ name: 'peakrentals', flag: '🇬🇧', ratings: [3.8, 4.1, 3.6] },
	{ name: 'horizoncars', flag: '🇬🇧', ratings: [4.0, 4.3, 3.7] },
	{ name: 'eurodrive', flag: '🇬🇧', ratings: [4.4, 4.6, 4.1] },
	{ name: 'swiftfleet', flag: '🇬🇧', ratings: [3.9, 4.2, 3.5] },
]

const COLUMNS = ['Overall', 'Service', 'Value']
const COLORS = ['#ef5350', '#66bb6a', '#ffa726', '#42a5f5', '#ab47bc']
const SITE_OPTIONS = ['Google Reviews', 'Trustpilot', 'TripAdvisor']


export default function RatingDemoData() {
	const maxs = COLUMNS.map((_, i) => Math.max(...DEMO.map(d => d.ratings[i])))
	const mins = COLUMNS.map((_, i) => Math.min(...DEMO.map(d => d.ratings[i])))

	const cellSx = (rating, colIdx) => ({
		textAlign: 'center', p: '10px 14px',
		...(rating === maxs[colIdx] && { background: '#e8f5e9', border: '2px solid #a5d6a7', borderRadius: 1 }),
		...(rating === mins[colIdx] && { background: '#ffebee', border: '2px solid #ef9a9a', borderRadius: 1 }),
	})

	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardHeader title={
					<Typography variant="subtitle1" fontWeight={600}>Analyzer: Price Table</Typography>
				} />

				{/* Toolbar: solo Toggle Details + Legend (isRating no tiene Features ni Min/Max) */}
				<Box sx={{ px: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="body2" fontWeight={500}>Hide Details</Typography>
						<Switch size="small" disabled />
					</Box>

					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#66bb6a' }} />
							<Typography variant="caption" color="text.secondary">Highest Rating in column</Typography>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef5350' }} />
							<Typography variant="caption" color="text.secondary">Lowest Rating in column</Typography>
						</Box>
					</Box>
				</Box>

				<Box sx={{ px: 3, pb: 2, overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600 } }}>
								<TableCell>Website</TableCell>
								{COLUMNS.map(c => <TableCell key={c} align="center">{c}</TableCell>)}
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO.map((d, rowIdx) => (
								<TableRow key={d.name}>
									<TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{d.flag} {d.name}</TableCell>
									{d.ratings.map((r, i) => (
										<TableCell
											key={i}
											sx={cellSx(r, i)}
											{...(rowIdx === 0 && i === 0 ? { 'data-tour-id': 'tour-price-cell' } : {})}
										>
											<Typography variant="body2" fontWeight={500}>
												{r.toFixed(1)} <Typography component="span" variant="caption" color="text.secondary">/ 5.0</Typography>
											</Typography>
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Card>
		</Grid>

		{/* Chart — isRating muestra Sites Autocomplete + Price values/Variation toggle */}
		<Grid item xs={12}>
			<Card sx={{ p: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
					<Autocomplete
						size="small"
						disabled
						options={SITE_OPTIONS}
						value={SITE_OPTIONS[0]}
						sx={{ width: 200 }}
						renderInput={(params) => <TextField {...params} label="Site" size="small" />}
					/>
					<ToggleButtonGroup size="small" exclusive value="price">
						<ToggleButton value="price">Price values</ToggleButton>
						<ToggleButton value="diff">Variation (%)</ToggleButton>
					</ToggleButtonGroup>
					<IconButton aria-label="Fullscreen" title="Fullscreen" size="small">
						<IconSeeker icon="expand" />
					</IconButton>
				</Box>

				<Box sx={{ height: 250, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: 2, px: 2 }}>
					{DEMO.map((d, idx) => {
						const h = (d.ratings[0] / 5.0) * 200
						return <Box key={d.name} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flex: 1 }}>
							<Box sx={{ width: '100%', maxWidth: 60, height: h, bgcolor: COLORS[idx], borderRadius: '4px 4px 0 0', opacity: 0.8 }} />
							<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{d.name}</Typography>
						</Box>
					})}
				</Box>
			</Card>
		</Grid>
	</Grid>
}
